import { EXPLORE_ROUTES } from "../exploreRoutes";
import React, { useMemo, useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import HomeDashboardLayout from "../HomeDashboardLayout";
import StartRapidHeader from "../rapidFire/StartRapidHeader";
import RapidAttemptCard from "../rapidFire/RapidAttemptCard";
import { useRapidFireBatchList } from "../../hooks/useRapidFireBatchList";
import { useRapidFireBatchCreate } from "../../hooks/useRapidFireBatchCreate";
import { RAPID_FIRE_ROUTES } from "./rapidFireRoutes";
import {
  buildRapidFireBackTarget,
  buildRapidFireFlowState,
  buildRapidFireSubjectsState,
  getStoredRapidFireFlowState,
  persistRapidFireFlowState,
} from "./rapidFireFlowState";
import "../../dashboard/Dashboard.css";
import "../HomeDashboard.css";
import "../questionBank/QuestionBankList.css";
import HeaderProfileSection from "../HeaderProfileSection";
import MyWalletSidebar from "../MyWalletSidebar";

const DEFAULT_STATE_ID = 13;
const DEFAULT_CATEGORY_ID = 5;
const DEFAULT_SUBJECT_ID = 50;

function RapidFireHeaderShimmer() {
  return (
    <div className="rapid-fire-card" aria-hidden="true">
      <div className="rapid-fire-header">
        <span className="dashboard-skeleton dashboard-skeleton--line dashboard-skeleton--title" style={{ width: "42%", height: 34 }} />
        <span className="dashboard-skeleton dashboard-skeleton--button" style={{ width: 110, height: 36, borderRadius: 999 }} />
      </div>

      <span className="dashboard-skeleton dashboard-skeleton--line" style={{ width: "30%", marginTop: 12 }} />
      <span className="dashboard-skeleton dashboard-skeleton--line" style={{ width: "100%", marginTop: 24 }} />
      <span className="dashboard-skeleton dashboard-skeleton--line" style={{ width: "92%", marginTop: 10 }} />
      <span className="dashboard-skeleton dashboard-skeleton--line" style={{ width: "84%", marginTop: 10 }} />

      <div className="start-header-btns" style={{ marginTop: 18 }}>
        <div className="rapid-fire-stats">
          {Array.from({ length: 3 }).map((_, index) => (
            <div key={index} className="rapid-fire-stat">
              <span className="dashboard-skeleton dashboard-skeleton--line" style={{ width: 28, height: 22 }} />
              <span className="dashboard-skeleton dashboard-skeleton--line" style={{ width: 72, height: 16 }} />
            </div>
          ))}
        </div>

        <div className="start-rapid-fire" style={{ cursor: "default" }}>
          <span className="dashboard-skeleton dashboard-skeleton--icon" style={{ width: 42, height: 42, borderRadius: 12 }} />
          <div className="start-rapid-fire-content" style={{ width: "100%" }}>
            <span className="dashboard-skeleton dashboard-skeleton--line" style={{ width: "58%", height: 18 }} />
            <span className="dashboard-skeleton dashboard-skeleton--line" style={{ width: "86%", height: 14, marginTop: 8 }} />
          </div>
          <span className="dashboard-skeleton dashboard-skeleton--icon" style={{ width: 28, height: 28, borderRadius: 999 }} />
        </div>
      </div>

      <span className="dashboard-skeleton dashboard-skeleton--line" style={{ width: "42%", marginTop: 18 }} />
      <span className="dashboard-skeleton dashboard-skeleton--line" style={{ width: "36%", marginTop: 8 }} />
    </div>
  );
}

function RapidFireAttemptCardShimmer() {
  return (
    <section className="attempteds-section rapid-attempt" aria-hidden="true">
      <div className="attempteds-heading mt-3">
        <h3>Attempts</h3>
      </div>

      <div className="instructionContent">
        <div className="quiz-inst-text">
          <div className="attempteds-grid">
            {Array.from({ length: 4 }).map((_, index) => (
              <div className="attempted-card rapidattempted-card" key={index}>
                <div className="attempted-top">
                  <span className="dashboard-skeleton dashboard-skeleton--button" style={{ width: 56, height: 56, borderRadius: 14 }} />
                  <div className="attempted-details">
                    <span className="dashboard-skeleton dashboard-skeleton--line" style={{ width: "52%", height: 18 }} />
                    <span className="dashboard-skeleton dashboard-skeleton--line" style={{ width: "68%", height: 12, marginTop: 8 }} />
                  </div>
                  <span className="dashboard-skeleton dashboard-skeleton--button" style={{ width: 82, height: 32, borderRadius: 18 }} />
                </div>

                <div className="attempted-progress" style={{ marginTop: 14 }}>
                  <span className="dashboard-skeleton dashboard-skeleton--line" style={{ width: "100%", height: 5, borderRadius: 10 }} />
                  <span className="dashboard-skeleton dashboard-skeleton--line" style={{ width: 32, height: 12 }} />
                </div>

                <div className="attempted-bottom" style={{ marginTop: 14 }}>
                  <span className="dashboard-skeleton dashboard-skeleton--line" style={{ width: "32%", height: 14 }} />
                  <span className="dashboard-skeleton dashboard-skeleton--line" style={{ width: "34%", height: 14 }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

const StartRapidFire = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const flowState = useMemo(
    () => buildRapidFireFlowState(location.state, getStoredRapidFireFlowState()),
    [location.state]
  );
  const selectedSubject = flowState?.selectedSubject || null;
  const selectedStateId = Number(flowState?.selectedState?.state_id || flowState?.state_id || DEFAULT_STATE_ID);
  const selectedCategoryId = Number(
    flowState?.selectedCategory?.category_id || flowState?.category_id || DEFAULT_CATEGORY_ID
  );
  const selectedSubjectId = Number(
    selectedSubject?.id || selectedSubject?.subject_id || flowState?.subject_id || DEFAULT_SUBJECT_ID
  );
  const subjectNavigationState = useMemo(
    () => buildRapidFireSubjectsState(flowState),
    [flowState]
  );
  const subjectBackTarget = useMemo(
    () => buildRapidFireBackTarget(EXPLORE_ROUTES.subjects, subjectNavigationState),
    [subjectNavigationState]
  );

  useEffect(() => {
    persistRapidFireFlowState({
      ...flowState,
      rapidFireBackTarget: subjectBackTarget,
    });
  }, [flowState, subjectBackTarget]);

  const { data, isLoading, isFetching, isError, error } = useRapidFireBatchList(
    {
      states_id: selectedStateId,
      category_id: selectedCategoryId,
      subject_id: selectedSubjectId,
    },
    {
      enabled:
        Number.isFinite(selectedStateId) &&
        selectedStateId > 0 &&
        Number.isFinite(selectedCategoryId) &&
        selectedCategoryId > 0 &&
        Number.isFinite(selectedSubjectId) &&
        selectedSubjectId > 0,
    }
  );
  const isPending = isLoading || isFetching;
  const loadError = isError
    ? error?.response?.data?.message || error?.message || "Unable to load rapid fire batches."
    : "";
  const headerTitle = data?.subjectName || selectedSubject?.title || "Rapid Fire";

  const [showCountdown, setShowCountdown] = useState(false);
  const [showLimitError, setShowLimitError] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const createBatchMutation = useRapidFireBatchCreate();

  useEffect(() => {
    let timer;
    if (showCountdown && countdown > 0) {
      timer = setInterval(() => {
        setCountdown((prev) => prev - 1);
      }, 1000);
    } else if (showCountdown && countdown === 0) {
      setShowCountdown(false);
      createBatchMutation
        .mutateAsync({
          states_id: selectedStateId,
          category_id: selectedCategoryId,
          subject_id: selectedSubjectId,
        })
        .then((response) => {
          navigate(RAPID_FIRE_ROUTES.detail, {
            replace: true,
            state: {
              ...flowState,
              rapidFireBackTarget: subjectBackTarget,
              batchData: response.payload,
            },
          });
        })
        .catch(() => {
          navigate(RAPID_FIRE_ROUTES.detail, {
            replace: true,
            state: {
              ...flowState,
              rapidFireBackTarget: subjectBackTarget,
            },
          });
        });
    }
    return () => clearInterval(timer);
  }, [
    showCountdown,
    countdown,
    navigate,
    flowState,
    createBatchMutation,
    selectedStateId,
    selectedCategoryId,
    selectedSubjectId,
    subjectBackTarget,
  ]);

  useEffect(() => {
    window.history.pushState({ rapidFireStartGuard: true }, "", window.location.href);

    const handlePopState = () => {
      navigate(EXPLORE_ROUTES.subjects, {
        replace: true,
        state: subjectNavigationState,
      });
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [navigate, subjectNavigationState]);

  const handleStart = () => {
    if (Number(data?.rapidFireStatus || 0) === 0) {
      setShowLimitError(true);
      return;
    }
    setCountdown(5);
    setShowCountdown(true);
  };

  return (
    <>
      <style>{`
          .homeDashboard-wrapper .page-content {
            padding-right: 0;
          }
      .homeDashboard-wrapper .main-content {
          height: 100vh;
          overflow: hidden;
          box-sizing: border-box;
          padding-bottom: 35px;
          min-height:0;
        }
        .rapid-fire-countdown-overlay {
          position: fixed;
          top: 0;
          left: 0;
          width: 100vw;
          height: 100vh;
          background: rgba(0, 0, 0, 0.7);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 9999;
          backdrop-filter: blur(4px);
        }
        .rapid-fire-countdown-modal {
          background: white;
          padding: 40px;
          border-radius: 20px;
          text-align: center;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
          width: 90%;
          max-width: 350px;
          animation: scaleIn 0.3s ease-out;
        }
        .rapid-fire-countdown-modal h3 {
          margin-bottom: 20px;
          color: #333;
          font-weight: 600;
        }
        .rapid-fire-countdown-timer {
          font-size: 72px;
          font-weight: 800;
          color: #68c900;
          line-height: 1;
        }
        @keyframes scaleIn {
          from { transform: scale(0.9); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
      `}</style>
      <HomeDashboardLayout showHeader={false}>
        {showCountdown && (
          <div className="rapid-fire-countdown-overlay">
            <div className="rapid-fire-countdown-modal">
              <h3>Get Ready!</h3>
              <div className="rapid-fire-countdown-timer">{countdown}</div>
            </div>
          </div>
        )}
        {showLimitError && (
          <div className="rapid-fire-countdown-overlay">
            <div className="rapid-fire-countdown-modal" style={{ padding: "30px 20px" }}>
              <h3 style={{ color: "#d9534f", marginBottom: 10 }}>Notice</h3>
              <p style={{ color: "#555", lineHeight: "1.5" }}>
                {data?.limitMessage || "You have reached your daily limit of 5 attempts. Please try again tomorrow."}
              </p>
              <button
                type="button"
                className="start-quiz-btn"
                style={{ marginTop: 25, width: "100%", height: 44, fontSize: 16 }}
                onClick={() => setShowLimitError(false)}
              >
                Okay
              </button>
            </div>
          </div>
        )}
        <div className="qbl-page qbankList-page page-padding ">
          <div className="start-quiz qbl-main instruction-main">
            <div className="hideProfile-page ">
              <div className="categories-page-header mb-0">
                <div>
                  <button
                    type="button"
                    className="categories-back-button"
                    onClick={() =>
                      navigate(EXPLORE_ROUTES.subjects, {
                        replace: true,
                        state: subjectNavigationState,
                      })
                    }
                    aria-label="Go back"
                  >
                    <i className="bi bi-chevron-left"></i>
                  </button>
                </div>
                <div>
                  <h4 className="d-flex d-sm-none categories-page-title">{headerTitle}</h4>
                </div>
                <div>
                  <HeaderProfileSection />
                </div>
              </div>
            </div>

            {isPending ? (
              <>
                <RapidFireHeaderShimmer />
                <RapidFireAttemptCardShimmer />
              </>
            ) : null}
            {!isPending && loadError ? (
              <p className="choose-state__status choose-state__status--error">{loadError}</p>
            ) : null}
            {!isPending && !loadError ? (
              <>
                <StartRapidHeader
                  title={data?.subjectName || headerTitle}
                  location={data?.stateName || "Himachal Pradesh"}
                  category={data?.categoryName || "Rapid Fire"}
                  description={data?.subjectDescription || ""}
                  available={Number(data?.rapidFireStatus || 0) === 1}
                  usedToday={Number(data?.usedToday || 0)}
                  leftToday={Number(data?.leftToday || 0)}
                  attempts={Array.isArray(data?.attempts) ? data.attempts.length : 0}
                  totalAttempts={Number(data?.totalAttempts || 5)}
                  limitMessage={data?.limitMessage || ""}
                  onStart={handleStart}
                />

                {(data?.attempts || []).length ? (
                  <RapidAttemptCard
                    attempts={data?.attempts || []}
                    navigationState={{
                      ...flowState,
                      rapidFireBackTarget: subjectBackTarget,
                    }}
                  />
                ) : (
                  <p className="choose-state__status">No rapid fire attempts available.</p>
                )}
              </>
            ) : null}
          </div>

          <MyWalletSidebar withTopOffset={false} />
        </div>
      </HomeDashboardLayout>
    </>
  );
};

export default StartRapidFire;
